/**
 * Preference question input UI by question type.
 *
 * Belongs here: question rendering + question-visible analytics.
 * Does not belong here: flow rules. Answer commit for multi-select is on prefs Next
 * (full selection → responseName), not each toggle — see expertReviewEvents contract.
 */
import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { EventNames } from '../../../../../../types/misc.types'
import { trackExpertReviewQuestionEvent } from '../../../analytics/expertReviewEvents'
import type { Question, QuestionAnswerValue, QuestionOption } from '../../../shared/types/questionnaire.types'
import { CheckedIcon, UnCheckedIcon } from '../../../shared/components/RadioOptionRow'
import RenderHTML from '../../../../../../components/ui-components/RenderHTML'
import { FundListItem } from '../header/FundListItem'
import { formatPortfolioShareAndFundCount } from '../../setup/config'
import { usePreferencesContext } from '../../flow-engine/PreferencesProvider'
import { ROW_CHEVRON_ICON } from './PreferenceInsightQuestion'
import { FundLabelWithSeparator } from '../header/FundLabelWithSeparator'
import { BorderWithPadding } from '../../../../plans-v4/lumpsum/plans-list/lumpsum-ui-components/BorderWithPadding'

/** FE-owned MULTI_SELECT_GROUPS chrome. Branch by preferenceTypeId when needed. */
export function getMultiSelectGroupsCopy(_preferenceTypeId?: string) {
  return {
    helperText: 'Unselected categories will be kept unchanged in your allocation plan',
    selectedTag: 'This category will be reallocated'
  }
}

interface MultiSelectGroupsQuestionProps {
  question: Question
  selectedOptionIds: string[]
  onToggleOption: (optionId: string) => void
  analyticsScreen: string
  contextId?: string
}

export function MultiSelectGroupsQuestion({
  question,
  selectedOptionIds,
  onToggleOption,
  analyticsScreen,
  contextId
}: MultiSelectGroupsQuestionProps) {
  const { currentPreferenceType } = usePreferencesContext()
  const copy = getMultiSelectGroupsCopy(currentPreferenceType?.id)
  const options = question.options ?? []
  const helperText = copy.helperText.trim()
  const [expandedOptionId, setExpandedOptionId] = useState<string | null>(null)
  const questionId = question.id

  useEffect(() => {
    setExpandedOptionId(null)
  }, [question.id])

  // Question visible — identity only; committed selection fires on prefs Next
  useEffect(() => {
    trackExpertReviewQuestionEvent({
      eventName: EventNames.ScreenLoaded,
      ScreenName: analyticsScreen,
      questionId,
      scrollSection: contextId
    })
  }, [analyticsScreen, questionId, contextId])

  function handleToggleExpand(optionId: string) {
    setExpandedOptionId((currentExpandedId) =>
      currentExpandedId === optionId ? null : optionId
    )
  }

  return (
    <div className="flex flex-col">
      <p className="text-[22px] font-[500] leading-[130%] text-[#232323] pb-4">
        {question.displayText}
      </p>

      <div className="overflow-hidden rounded-[16px] border border-[#F0F4F7] bg-white">
        {options.map((option, optionIndex) => {
          const isSelected = selectedOptionIds.includes(option.id)
          const fundList = option.fundList ?? []
          return (
            <MultiSelectGroupOptionRow
              key={option.id}
              option={option}
              isSelected={isSelected}
              selectedTag={copy.selectedTag}
              isExpanded={expandedOptionId === option.id && fundList.length > 0}
              showSeparator={optionIndex < options.length - 1}
              onToggleSelect={() => onToggleOption(option.id)}
              onToggleExpand={() => handleToggleExpand(option.id)}
            />
          )
        })}
      </div>

      {helperText ? (
        <p className="text-[13px] font-[400] leading-[150%] text-[#6A6E6E] pt-3">{helperText}</p>
      ) : null}
    </div>
  )
}

export function MultiSelectGroupOptionRow({
  option,
  isSelected,
  selectedTag,
  isExpanded,
  showSeparator,
  onToggleSelect,
  onToggleExpand
}: {
  option: QuestionOption
  isSelected: boolean
  selectedTag: string
  isExpanded: boolean
  showSeparator: boolean
  onToggleSelect: () => void
  onToggleExpand: () => void
}) {
  const fundList = option.fundList ?? []
  const metaLabel = formatPortfolioShareAndFundCount(option.portfolioShare, fundList.length)
  const canExpand = fundList.length > 0

  return (
    <div>
      <div className="relative flex items-start gap-3 py-[22px] pl-4 pr-5">
        {/* Visual row — clicks handled by absolute halves above */}
        <div className="pointer-events-none flex min-w-0 flex-1 items-start gap-3">
          {/* 32px icon; glyph is centered in the box — nudge so it sits on the title line */}
          <div className="shrink-0">
            <RenderHTML
              innerHTMLText={isSelected ? CheckedIcon : UnCheckedIcon}
              className="[&>svg]:block"
            />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-[500] leading-[150%] text-[#232323]">{option.displayText}</p>
            <p className="pt-1 text-[14px] font-[400] leading-[130%] text-[#232323] opacity-60 flex flex-wrap items-center">
              <FundLabelWithSeparator label={metaLabel} />
            </p>
            {isSelected && selectedTag !== '' ? (
              <p className="pt-1 text-[14px] font-[500] leading-[130%] text-[#004D99]">{selectedTag}</p>
            ) : null}
          </div>
        </div>
        {canExpand ? (
          <motion.div
            initial={false}
            animate={{ rotate: isExpanded ? -90 : 90 }}
            transition={{ duration: 0.35 }}
            className="pointer-events-none mt-[2.5px] flex h-4 w-4 shrink-0 items-center justify-center"
          >
            <RenderHTML
              innerHTMLText={ROW_CHEVRON_ICON}
              className="h-4 w-4 [&>svg]:block [&>svg]:h-full [&>svg]:w-full"
            />
          </motion.div>
        ) : null}

        {/* Click targets: left = select, right = expand (or select if no funds) */}
        <div className="absolute inset-0 flex">
          <div onClick={onToggleSelect} className="h-full w-1/2 cursor-pointer" />
          <div
            onClick={canExpand ? onToggleExpand : onToggleSelect}
            className="h-full w-1/2 cursor-pointer"
          />
        </div>
      </div>
      <AnimatePresence>
        {isExpanded ? (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ type: 'spring', duration: 0.4, bounce: 0 }}
            className="flex w-full flex-col overflow-hidden"
          >
            {fundList.map((fund) => (
              <div key={fund.isin} className="bg-white">
                <BorderWithPadding padding="pl-16" />
                <div className="pl-3 pr-5">
                  <FundListItem fund={fund} truncateName={false} />
                </div>
              </div>
            ))}
          </motion.div>
        ) : null}
      </AnimatePresence>
      {showSeparator ? <BorderWithPadding padding="pl-0" /> : null}
    </div>
  )
}

export function getSelectedOptionIds(value: QuestionAnswerValue | undefined): string[] {
  if (Array.isArray(value)) return value.map(String)
  if (typeof value === 'string' && value !== '') return [value]
  return []
}

export function toggleSelectedOptionId(selectedOptionIds: string[], optionId: string): string[] {
  if (selectedOptionIds.includes(optionId)) {
    return selectedOptionIds.filter((selectedId) => selectedId !== optionId)
  }
  return [...selectedOptionIds, optionId]
}
